import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SectionLoader } from "@/components/common";
import { Loader2, Upload, X, Plus, Briefcase } from "lucide-react";

export default function Business() {
  const [form, setForm] = useState(null);
  const [serviceInput, setServiceInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/networking/me").then((r) => {
      const d = r.data || {};
      setForm({
        business_name: d.business_name || "", tagline: d.tagline || "", industry: d.industry || "",
        description: d.description || "", website: d.website || "", phone: d.phone || "",
        email: d.email || "", linkedin: d.linkedin || "", logo: d.logo || "",
        services: d.services || [], looking_for: d.looking_for || "",
        visible: d.visible !== undefined ? d.visible : true,
      });
    });
  }, []);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please choose an image under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "logos");
      const res = await api.post("/storage/upload", fd);
      setForm((f) => ({ ...f, logo: res.data.url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Upload failed — please try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addService = () => {
    const v = serviceInput.trim();
    if (v && !form.services.includes(v)) setForm({ ...form, services: [...form.services, v] });
    setServiceInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/networking/me", form);
      toast.success("Business profile saved");
    } catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (!form) return <SectionLoader />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">My Business</h1>
        <p className="mt-1 text-muted-foreground">Share what you do so members can refer business your way.</p>
      </div>

      <Card className="border-border bg-card"><CardContent className="space-y-5 p-6">
        <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/60 p-4">
          <div><p className="font-medium text-foreground">Show my business in the directory</p><p className="text-sm text-muted-foreground">Turn off to keep your business details private.</p></div>
          <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="business-visible-toggle" />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary">
            {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-cover" /> : <Briefcase className="h-6 w-6 text-muted-foreground" />}
          </div>
          <div>
            <input id="logo" type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="business-logo-input" />
            <Button asChild variant="secondary" size="sm" disabled={uploading}><label htmlFor="logo" className="cursor-pointer">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{uploading ? "Uploading…" : "Upload logo"}</label></Button>
            <p className="mt-1 text-xs text-muted-foreground">PNG or JPG, up to 10MB. Stored on Cloudflare R2.</p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Business name</Label><Input className="mt-1.5" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="business-name-input" /></div>
          <div><Label>Industry</Label><Input className="mt-1.5" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Real Estate" /></div>
          <div className="sm:col-span-2"><Label>Tagline</Label><Input className="mt-1.5" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="One line about your business" /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea className="mt-1.5" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label>Website</Label><Input className="mt-1.5" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
          <div><Label>Business email</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Business phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>LinkedIn</Label><Input className="mt-1.5" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} /></div>
        </div>

        <div>
          <Label>Services / specialties</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); } }} placeholder="Add a service and press Enter" data-testid="business-service-input" />
            <Button type="button" variant="secondary" onClick={addService}><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {form.services.map((s) => (
              <Badge key={s} variant="secondary" className="gap-1">{s}<button onClick={() => setForm({ ...form, services: form.services.filter((x) => x !== s) })}><X className="h-3 w-3" /></button></Badge>
            ))}
          </div>
        </div>

        <div><Label>What I'm looking for</Label><Textarea className="mt-1.5" rows={2} value={form.looking_for} onChange={(e) => setForm({ ...form, looking_for: e.target.value })} placeholder="Ideal referrals, partnerships, etc." /></div>

        <Button onClick={save} disabled={saving || uploading} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="business-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save business profile"}</Button>
      </CardContent></Card>
    </div>
  );
}
